import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Trash2, Check, PlusSquare } from "lucide-react";

export type FileUploadProps = {
  onVideoUpload?: (file: File) => void;
};

type PreviewFile = {
  file: File;
  preview: string;
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

export default function FileUpload({ onVideoUpload }: FileUploadProps) {
  const [files, setFiles] = useState<PreviewFile[]>([]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const allFiles = [...files.map((f) => f.file), ...acceptedFiles];
      const hasVideo = allFiles.some((file) => file.type.startsWith("video/"));
      const hasImage = allFiles.some((file) => file.type.startsWith("image/"));
      const videoCount = allFiles.filter((f) =>
        f.type.startsWith("video/")
      ).length;

      if (hasVideo && hasImage) {
        console.warn("Cannot upload image and video together");
        return;
      }

      if (videoCount > 1) {
        console.warn("Multiple video upload is not allowed");
        return;
      }

      if (
        acceptedFiles.length === 1 &&
        acceptedFiles[0].type.startsWith("video/")
      ) {
        console.log("Video uploaded");
        onVideoUpload?.(acceptedFiles[0]);
      }

      const previewFiles = acceptedFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      setFiles((prev) => [...prev, ...previewFiles]);
    },
    [files, onVideoUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
      "video/*": [],
    },
    multiple: true,
  });

  const handleRemove = (indexToRemove: number) => {
    setFiles((prev) => {
      const updated = [...prev];
      const toRemove = updated[indexToRemove];
      if (toRemove) URL.revokeObjectURL(toRemove.preview);
      updated.splice(indexToRemove, 1);
      return updated;
    });
  };

  useEffect(() => {
    return () => {
      files.forEach((f) => URL.revokeObjectURL(f.preview));
    };
  }, [files]);

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`min-h-[200px] p-4 border-2 border-dashed rounded-xl transition-all ${
          isDragActive
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 bg-white"
        } cursor-pointer flex flex-col items-center justify-center`}
      >
        <input {...getInputProps()} />
        {files.length === 0 ? (
          <div className="text-center text-gray-600 space-y-2">
            <div className="flex justify-center">
              <div className="bg-gray-200 p-3 rounded-full">
                <PlusSquare size={28} className="text-gray-700" />
              </div>
            </div>
            <p className="text-base font-medium">Add photos/videos</p>
            <p className="text-sm text-gray-500">or drag and drop</p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-4 w-full justify-start">
            {files.map(({ file, preview }, idx) => (
              <div
                key={`${file.name}-${file.size}`}
                className="relative flex w-64 h-24 border rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <div className="absolute top-1 left-1 bg-blue-500 rounded-full p-1 z-10">
                  <Check size={12} className="text-white" />
                </div>
                <div className="w-24 h-full shrink-0 bg-gray-100">
                  <img
                    src={preview}
                    alt={file.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex flex-col justify-center px-3 py-2 flex-1 truncate">
                  <p className="text-sm font-medium truncate">{file.name}</p>
                  <p className="text-xs text-blue-600">
                    {formatBytes(file.size)}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(idx);
                  }}
                  className="p-2 hover:text-red-600 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

